import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { transitionDurationFast, transitionDurationBase, transitionDurationSlow } from '../variables';
import Base, { Props as BaseProps } from '../Base/Base';
import './Appear.css';

const isClient = typeof window !== 'undefined';

const durations = {
  fast: transitionDurationFast,
  base: transitionDurationBase,
  slow: transitionDurationSlow,
};

export type TypeAppearAnimationName =
  'Fade' |
  'FadeSlideUp' |
  'FadeSlideRight' |
  'FadeSlideDown' |
  'FadeSlideLeft';

export interface Props extends BaseProps {
  /** Type of enter and exit animation */
  animation: TypeAppearAnimationName;
  className?: string;
  /** Entrance delay time */
  delay?: number;
  /** Entrance duration */
  duration?: 'slow' | 'base' | 'fast';
  style?: React.CSSProperties;
  /** Flag if the element is visible or not */
  visible?: boolean;
}

/** Enter and exit components into view with a bit of bedazzle */
const Appear: React.FunctionComponent<Props> = (props: Props) => {
  const {
    animation,
    className,
    delay,
    duration = 'fast',
    visible,
    ...rest
  } = props;

  const classes = classnames(className, 'Appear', `Appear--${animation}`, `Appear--${duration}`, {
    [`Appear--${animation}-appear`]: !isClient && visible,
  });

  const transition = {
    appear: `Appear--${animation}-appear`,
    appearActive: `Appear--${animation}-appear-active`,
    enter: `Appear--${animation}-enter`,
    enterActive: `Appear--${animation}-enter-active`,
    enterDone: `Appear--${animation}-enter-done`,
    exit: `Appear--${animation}-exit`,
    exitActive: `Appear--${animation}-exit-active`,
    exitDone: `Appear--${animation}-exit-done`,
  };

  const style = {
    ...props.style,
    animationDelay: `${delay}ms`,
    transitionDelay: `${delay}ms`,
  };

  return (
    <CSSTransition
        appear={ isClient && visible }
        classNames={ transition }
        in={ isClient && visible }
        timeout={ durations[duration] }>
      <Base { ...rest } className={ classes } style={ style } />
    </CSSTransition>
  );
};

Appear.defaultProps = {
  delay: 0,
  duration: 'fast',
  visible: true,
};

export default Appear;
